import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Container, Skeleton } from "@mui/material";
import Navbar from "../../../Navbar";
import Title from "../../../Title";
import { useToast } from "../../../../Hooks/UseToast";
import { getSemana } from "../../common/utils/semana";
import { useDisponiblesSemana } from "../hooks/useDisponiblesSemana";
import { useMisPedidosSemana } from "../hooks/useMisPedidosSemana";
import {
  crearPedido,
  actualizarPedido,
  cancelarPedido,
} from "../service/empleadoViandasService";
import SemanaNavigator from "../components/SemanaNavigator";
import BloqueadoBanner from "../components/BloqueadoBanner";
import SemanaSoloLecturaBanner from "../components/SemanaSoloLecturaBanner";
import DiaViandaCard from "../components/DiaViandaCard";
import ProgresoSemanaBanner from "../components/ProgresoSemanaBanner";

const MisViandasPage = () => {
  // Solo se puede pedir/modificar para la semana que viene: la semana en
  // curso y las pasadas quedan en modo consulta una vez que arrancaron.
  const [weekOffset, setWeekOffset] = useState(1);
  const [busyFecha, setBusyFecha] = useState(null);
  const [bloqueado, setBloqueado] = useState(false);
  const { showToast, Toast } = useToast();

  const semana = useMemo(() => getSemana(weekOffset), [weekOffset]);
  const semanaEditable = weekOffset >= 1;

  const {
    data: disponibles,
    loading: loadingDisponibles,
    refresh: refreshDisponibles,
  } = useDisponiblesSemana(semana.desde, semana.hasta);

  const {
    data: pedidos,
    loading: loadingPedidos,
    refresh: refreshPedidos,
  } = useMisPedidosSemana(semana.desde, semana.hasta);

  const loading = loadingDisponibles || loadingPedidos;

  const disponiblesPorFecha = useMemo(() => {
    const map = {};
    disponibles.forEach((m) => {
      if (!map[m.fecha]) map[m.fecha] = [];
      map[m.fecha].push(m);
    });
    return map;
  }, [disponibles]);

  const pedidosPorFecha = useMemo(() => {
    const map = {};
    pedidos.forEach((p) => {
      map[p.fecha] = p;
    });
    return map;
  }, [pedidos]);

  const diasConMenu = semana.dias.filter((d) => disponiblesPorFecha[d.fecha]?.length);
  const diasElegidos = diasConMenu.filter((d) => pedidosPorFecha[d.fecha]);

  const completoRef = useRef(null);
  useEffect(() => {
    if (loading) return;
    const completo = diasConMenu.length > 0 && diasElegidos.length === diasConMenu.length;
    if (completoRef.current === false && completo) {
      showToast(`¡Semana completa! Elegiste tus ${diasConMenu.length} viandas 🎉`, "success");
    }
    completoRef.current = completo;
  }, [loading, diasElegidos.length, diasConMenu.length]);

  const ejecutarAccion = async (fecha, fn) => {
    setBusyFecha(fecha);
    try {
      await fn();
      setBloqueado(false);
      await Promise.all([refreshDisponibles(), refreshPedidos()]);
    } catch (err) {
      if (err.status === 403) setBloqueado(true);
      showToast(err.message || "Ocurrió un error inesperado.", "error");
    } finally {
      setBusyFecha(null);
    }
  };

  const handleElegir = (fecha, idMenu) =>
    ejecutarAccion(fecha, async () => {
      await crearPedido(idMenu, fecha);
      showToast("Vianda seleccionada correctamente.", "success");
    });

  const handleCambiar = (fecha, idPedido, idMenu) =>
    ejecutarAccion(fecha, async () => {
      await actualizarPedido(idPedido, idMenu);
      showToast("Vianda actualizada correctamente.", "success");
    });

  const handleCancelar = (fecha, idPedido) =>
    ejecutarAccion(fecha, async () => {
      await cancelarPedido(idPedido);
      showToast("Pedido cancelado.", "info");
    });

  return (
    <>
      <Navbar />
      <Title titulo="Mis Viandas" handleClose={() => window.history.back()} />

      <Container maxWidth="xl" sx={{ mt: 3, mb: 6 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <SemanaNavigator
            desde={semana.desde}
            hasta={semana.hasta}
            esSemanaActual={semana.esSemanaActual}
            onPrev={() => setWeekOffset((o) => o - 1)}
            onNext={() => setWeekOffset((o) => o + 1)}
            onHoy={() => setWeekOffset(0)}
          />

          {bloqueado && <BloqueadoBanner />}
          {!bloqueado && !semanaEditable && (
            <SemanaSoloLecturaBanner esPasada={weekOffset < 0} />
          )}

          {!loading && diasConMenu.length > 0 && (
            <ProgresoSemanaBanner
              dias={diasConMenu}
              diasElegidosFechas={diasElegidos.map((d) => d.fecha)}
            />
          )}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(5, 1fr)",
              },
              gap: 2,
              mb: 3,
            }}
          >
            {loading &&
              semana.dias.map((d) => (
                <Skeleton
                  key={d.fecha}
                  variant="rounded"
                  height={220}
                  sx={{ borderRadius: 4 }}
                />
              ))}

            {!loading &&
              semana.dias.map((dia) => (
                <DiaViandaCard
                  key={dia.fecha}
                  dia={dia}
                  menus={disponiblesPorFecha[dia.fecha] || []}
                  pedido={pedidosPorFecha[dia.fecha]}
                  onElegir={handleElegir}
                  onCambiar={(idPedido, idMenu) => handleCambiar(dia.fecha, idPedido, idMenu)}
                  onCancelar={(idPedido) => handleCancelar(dia.fecha, idPedido)}
                  disabled={bloqueado || !semanaEditable}
                  busy={busyFecha === dia.fecha}
                />
              ))}
          </Box>
        </Box>
      </Container>

      {Toast}
    </>
  );
};

export default MisViandasPage;
